import React, { useState, useEffect, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { getApiUrl } from "../config/api";
import { formatDateTime } from "../utils/dateUtils";
import Settings from "./Settings";
import HistoryDetailsModal from "./HistoryDetailsModal";
import { useSettings } from "../context/SettingsContext";

export default function Header({ isAdmin = false, clientId, openChatForRfq }) {
  const { user, logout } = useAuth0();
  const { settings, playNotificationSound, playQuoteSound } = useSettings();
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [streamStatus, setStreamStatus] = useState("idle");
  const [selectedRFQ, setSelectedRFQ] = useState(null);
  const [showRFQModal, setShowRFQModal] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    let es;
    let retryTimeout;

    const handleMessage = (event) => {
      try {
        const raw = JSON.parse(event.data);
        
        // Check if notifications are enabled
        if (!settings.generalSettings.enableNotifications) {
          return; // Skip notification if disabled
        }
        
        // Handle new notification structure
          // notification payload may place RFQ info either directly on the notification
          // or nested inside a `metadata` object. Support both shapes.
        const notificationData = raw.notification || raw;
        const metadata = notificationData.metadata || {};

        const rfqNumber = metadata.rfqNumber || notificationData.rfqNumber || raw.rfqNumber || null;
        const rfqId = metadata.rfqId || notificationData.rfqId || raw.rfqId || null;

        const notification = {
          id: notificationData.id || raw.id || `notif-${Date.now()}`,
          type: notificationData.type || "general",
          title: notificationData.title || (notificationData.type === 'chat' ? 'Chat Message' : 'Notification'),
          message: notificationData.message || raw.message || "You have a new notification",
          timestamp: notificationData.createdAt || raw.timestamp || new Date().toISOString(),
          read: notificationData.read || false,
          // RFQ info (try metadata first, then top-level fields)
          rfqNumber,
          rfqId,
          quoteId: metadata.quoteId || notificationData.quoteId || raw.quoteId || null,
          state: metadata.state || notificationData.state || raw.state || null,
          // Sender info (helpful for chat notifications)
          senderName: notificationData.senderName || notificationData.from || raw.senderName || null,
        };
        setNotifications((prev) => [notification, ...prev].slice(0, 50));
        
        // Play appropriate sound based on notification type
        if (notification.type === 'quote') {
          playQuoteSound();
        } else {
          playNotificationSound();
        }
      } catch (err) {
        console.warn("Notification SSE parse error:", err, event?.data);
      }
    };

    const connect = () => {
      // Build stream URL and include clientId if provided
      const streamUrl = clientId
        ? getApiUrl(`/api/notifications/stream?clientId=${encodeURIComponent(clientId)}`)
        : getApiUrl('/api/notifications/stream');

      setStreamStatus("connecting");
      
      es = new EventSource(streamUrl);
      
      es.onopen = () => {
        setStreamStatus("open");
      };
      
      es.onmessage = handleMessage;
      es.addEventListener("notification", handleMessage);
      
      es.onerror = (error) => {
        setStreamStatus("error");
        try { es.close(); } catch {}
        retryTimeout = setTimeout(() => {
          connect();
        }, 3000);
      };
    };

    connect();

    return () => {
      if (es) {
        try { es.close(); } catch {}
      }
      if (retryTimeout) clearTimeout(retryTimeout);
    };
  }, [playNotificationSound, playQuoteSound, settings.generalSettings.enableNotifications, clientId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };

    if (showNotifications) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showNotifications]);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const handleNotificationClick = async (notification) => {
    // Mark as read
    markAsRead(notification.id);
    
    // If it's an RFQ notification, load and show the RFQ details
    // If it's a chat notification (or contains rfq info), open the chat directly
    if (notification.type === 'chat' || notification.rfqNumber || notification.rfqId) {
      // Prefer using the rfqNumber if available
      const rfqPayload = {
        rfqNumber: notification.rfqNumber || null,
        rfqId: (notification.rfqId != null) ? notification.rfqId : null,
        dealerName: notification.senderName || notification.title || 'Company',
        dealerId: null,
      };
      // Use App-level handler to open or create the chat
      if (openChatForRfq) {
        openChatForRfq(rfqPayload);
        setShowNotifications(false);
        return;
      }
    }

    if (notification.rfqNumber) {
      try {
        // Fetch RFQ details from history
        const response = await fetch(getApiUrl(`/api/history/client/${encodeURIComponent(clientId)}`));
        if (!response.ok) throw new Error('Failed to fetch RFQ details');
        
        const data = await response.json();
        const historyItems = Array.isArray(data) ? data : data?.history ?? data?.items ?? data?.newHistory ?? [];
        
        // Find the RFQ with matching rfqNumber
        const rfqItem = historyItems.find(item => 
          (item.rfq?.rfqNumber === notification.rfqNumber) || 
          (item.rfqNumber === notification.rfqNumber)
        );
        
        if (rfqItem) {
          // Normalize the item structure
          const rfq = rfqItem.rfq || {};
          const quote = rfqItem.quote || null;
          
          const normalizedItem = {
            id: rfqItem.id || `rfq-${Date.now()}`,
            event: rfqItem.event || "RFQ",
            dealer: rfqItem.dealerName || rfqItem.dealer?.name || "Unknown dealer",
            state: rfq.location?.state || rfq.state || rfqItem.location?.state || rfqItem.state || "N/A",
            quantity: rfq.quantity ?? rfqItem.quantity ?? -1,
            material: rfq.material || rfqItem.material || "N/A",
            subMaterial: rfq.subtype || rfqItem.subtype || "",
            materialCategory: rfq.materialCategory || rfqItem.materialCategory || "",
            rfqNumber: rfq.rfqNumber || rfqItem.rfqNumber,
            status: rfq.status || rfqItem.status,
            time: rfqItem.createdAt || new Date().toISOString(),
            quote: quote ? {
              id: quote.id,
              price: quote.price,
              deliveryTime: quote.deliveryTime,
              terms: quote.terms,
              notes: quote.notes,
              validUntil: quote.validUntil,
              createdAt: quote.createdAt,
              updatedAt: quote.updatedAt,
            } : null,
          };
          
          setSelectedRFQ(normalizedItem);
          setShowRFQModal(true);
          setShowNotifications(false);
        } else {
          console.warn('[Header] RFQ not found:', notification.rfqNumber);
          alert('RFQ details not found');
        }
      } catch (error) {
        console.error('[Header] Failed to load RFQ details:', error);
        alert('Failed to load RFQ details');
      }
    }
  };

  const markAsRead = (id) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
  };

  const markAllAsRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  const handleRFQModalClose = () => {
    setShowRFQModal(false);
    setSelectedRFQ(null);
  };

  const handleRFQUpdate = (updatedItem) => {
    // Update the selected RFQ if needed
    setSelectedRFQ(updatedItem);
  };

  const statusColor =
    streamStatus === "open"
      ? "bg-green-500"
      : streamStatus === "connecting"
      ? "bg-yellow-500"
      : streamStatus === "error"
      ? "bg-red-500"
      : "bg-gray-500";

  return (
    <header className="bg-gray-800 dark:bg-gray-950 text-white p-4 flex justify-between items-center border-b border-gray-700 dark:border-gray-800">
      <img 
        src="/GBMEx Logo.png" 
        alt="GBMEx" 
        className="h-16"
      />
      <div className="flex items-center gap-4">
        {/* User Info */}
        <div className="flex items-center gap-3 px-3 py-2 bg-gray-700 dark:bg-gray-800 rounded">
          {user?.picture && (
            <img 
              src={user.picture} 
              alt={user?.name || "User"} 
              className="w-8 h-8 rounded-full"
            />
          )}
          <div className="text-sm">
            <div className="font-medium flex items-center gap-2">
              {user?.name || "User"}
              {isAdmin && (
                <span className="px-2 py-0.5 bg-yellow-500 text-yellow-900 text-xs font-bold rounded">
                  ADMIN
                </span>
              )}
            </div>
            <div className="text-xs text-gray-300 dark:text-gray-400">{user?.email}</div>
          </div>
        </div>

        {/* Notification Bell */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setShowNotifications((s) => !s)}
            className="relative p-2 hover:bg-gray-700 dark:hover:bg-gray-800 rounded transition-colors"
            title={`Notifications • ${streamStatus}`}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
              />
            </svg>
            {/* Health status indicator */}
            <span className={`absolute top-0 left-0 w-2 h-2 rounded-full ${statusColor}`} title={streamStatus} />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </button>

          {/* Notification Dropdown */}
          {showNotifications && (
            <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg shadow-lg border border-gray-300 dark:border-gray-700 z-50 max-h-96 overflow-hidden flex flex-col">
              <div className="p-3 border-b border-gray-300 dark:border-gray-700 flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold">Notifications</h3>
                  <span
                    className={`text-xs px-2 py-0.5 rounded ${
                      streamStatus === "open"
                        ? "bg-green-200 dark:bg-green-900 text-green-800 dark:text-green-200"
                        : streamStatus === "connecting"
                        ? "bg-yellow-200 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200"
                        : streamStatus === "error"
                        ? "bg-red-200 dark:bg-red-900 text-red-800 dark:text-red-200"
                        : "bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                    }`}
                    title="Stream status"
                  >
                    {streamStatus}
                  </span>
                </div>
                {unreadCount > 0 && (
                  <button
                    onClick={markAllAsRead}
                    className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                  >
                    Mark all read
                  </button>
                )}
              </div>
              <div className="overflow-y-auto">
                {notifications.length === 0 && (
                  <div className="p-4 text-sm text-gray-500 dark:text-gray-400 text-center">
                    No notifications yet.
                  </div>
                )}
                {notifications.map((n) => (
                  <button
                    key={n.id}
                    onClick={() => handleNotificationClick(n)}
                    className={`w-full text-left p-3 border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                      !n.read ? "bg-blue-50 dark:bg-blue-900/20" : ""
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      {!n.read && (
                        <span className="w-2 h-2 bg-blue-600 dark:bg-blue-400 rounded-full mt-1.5 flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm">{n.title}</div>
                        <div className="text-sm text-gray-700 dark:text-gray-300">
                          {n.message}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {formatDateTime(n.timestamp)}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <button 
          onClick={() => setShowSettings(true)}
          className="bg-gray-700 dark:bg-gray-800 hover:bg-gray-600 dark:hover:bg-gray-700 px-4 py-2 rounded transition-colors"
        >
          Settings
        </button>

        <button 
          onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
          className="bg-red-600 dark:bg-red-700 hover:bg-red-700 dark:hover:bg-red-800 px-4 py-2 rounded transition-colors flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
          Logout
        </button>
      </div>

      {/* Settings Modal */}
      <Settings isOpen={showSettings} onClose={() => setShowSettings(false)} />
      
      {/* RFQ Details Modal */}
      {showRFQModal && selectedRFQ && (
        <HistoryDetailsModal
          item={selectedRFQ}
          onClose={handleRFQModalClose}
          onUpdate={handleRFQUpdate}
          clientId={clientId}
          onOpenChat={(rfq) => {
            if (openChatForRfq) openChatForRfq(rfq);
            handleRFQModalClose();
          }}
        />
      )}
    </header>
  );
}
